import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useMutation';
import { useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';

export interface ShiftLocation {
  latitude: number;
  longitude: number;
  accuracyMeters: number;
  locationSource: string;
  locationLabel: string;
}

export interface Shift {
  id: string;
  status: 'ACTIVE' | 'PAUSED' | 'ENDED';
  createdAt: string;
  startedAt: string;
  endedAt: string | null;
  startLatitude: number | null;
  startLongitude: number | null;
  endLatitude: number | null;
  endLongitude: number | null;
  endReason: string | null;
  lastPausedAt: string | null;
}

export interface ShiftsResponse {
  success: boolean;
  message: string;
  data: Shift[];
}

export interface UseShiftReturn {
  shifts: Shift[] | undefined;
  currentShift: Shift | undefined;
  isLoading: boolean;
  error: any;
  refetch: () => void;
  startShift: (location: ShiftLocation) => Promise<void>;
  pauseShift: (shiftId: string, location: ShiftLocation) => Promise<void>;
  endShift: (shiftId: string, location: ShiftLocation) => Promise<void>;
  resumeShift: (shiftId: string, location: ShiftLocation) => Promise<void>;
  sendHeartbeat: (shiftId: string, location: ShiftLocation) => Promise<void>;
  isStartingShift: boolean;
  isPausingShift: boolean;
  isEndingShift: boolean;
  isResumingShift: boolean;
  isSendingHeartbeat: boolean;
}

export function useShift(): UseShiftReturn {
  const {
    data: shiftsResponse,
    isLoading,
    error,
    refetch,
  } = useApiQuery<ShiftsResponse>({
    queryKey: ['shifts'],
    request: {
      method: 'GET',
      url: '/partner/shifts',
    },
  });

  const shifts = shiftsResponse?.data;
  const currentShift = shifts?.find(shift => shift.status === 'ACTIVE' || shift.status === 'PAUSED');

  const startShiftMutation = useApiMutation<any>({
    onSuccess: () => {
      refetch();
    },
  });

  const pauseShiftMutation = useApiMutation<any>({
    onSuccess: () => {
      refetch();
    },
  });

  const endShiftMutation = useApiMutation<any>({
    onSuccess: () => {
      refetch();
    },
  });

  const resumeShiftMutation = useApiMutation<any>({
    onSuccess: () => {
      refetch();
    },
  });

  const heartbeatMutation = useApiMutation<any>({
    onSuccess: () => {
      console.log('Heartbeat sent successfully');
      toast.success('Current location has been uploaded!');
    },
    onError: (error) => {
      console.error('Failed to send heartbeat:', error);
      toast.error('Failed to send heartbeat');
    },
  });

  const startShift = async (location: ShiftLocation) => {
    await startShiftMutation.mutateAsync({
      method: 'POST',
      url: '/partner/shifts/start',
      data: location,
    });
  };

  const pauseShift = async (shiftId: string, location: ShiftLocation) => {
    await pauseShiftMutation.mutateAsync({
      method: 'POST',
      url: `/partner/shifts/${shiftId}/pause`,
      data: location,
    });
  };

  const endShift = async (shiftId: string, location: ShiftLocation) => {
    await endShiftMutation.mutateAsync({
      method: 'POST',
      url: `/partner/shifts/${shiftId}/end`,
      data: location,
    });
  };

  const resumeShift = async (shiftId: string, location: ShiftLocation) => {
    await resumeShiftMutation.mutateAsync({
      method: 'POST',
      url: `/partner/shifts/${shiftId}/heartbeat`,
      data: location,
    });
  };

  const sendHeartbeat = useCallback(async (shiftId: string, location: ShiftLocation) => {
    await heartbeatMutation.mutateAsync({
      method: 'POST',
      url: `/partner/shifts/${shiftId}/heartbeat`,
      data: location,
    });
  }, []);

  return {
    shifts,
    currentShift,
    isLoading,
    error,
    refetch,
    startShift,
    pauseShift,
    endShift,
    resumeShift,
    sendHeartbeat,
    isStartingShift: startShiftMutation.isPending,
    isPausingShift: pauseShiftMutation.isPending,
    isEndingShift: endShiftMutation.isPending,
    isResumingShift: resumeShiftMutation.isPending,
    isSendingHeartbeat: heartbeatMutation.isPending,
  };
}

export function useHeartbeat(currentShift: Shift | undefined, sendHeartbeat: (shiftId: string, location: ShiftLocation) => Promise<void>) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get current location
  const getCurrentLocation = (): Promise<ShiftLocation> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyMeters: position.coords.accuracy,
            locationSource: 'GPS',
            locationLabel: 'Current Location',
          });
        },
        (error) => {
          console.warn('Geolocation failed, using default location:', error);
          reject(new Error('Geolocation failed: ' + error.message));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000,
        }
      );
    });
  };

  const sendHeartbeatWithLocation = useCallback(async () => {
    if (!currentShift || currentShift.status !== 'ACTIVE') {
      return;
    }

    try {
      const location = await getCurrentLocation();
      await sendHeartbeat(currentShift.id, location);
    } catch (error) {
      console.error('Failed to send heartbeat:', error);
    }
  }, [currentShift, sendHeartbeat]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (currentShift && currentShift.status === 'ACTIVE') {
      
      // Then send heartbeat every 10 minutes (600000 ms)
      intervalRef.current = setInterval(() => {
        sendHeartbeatWithLocation();
      }, 10 * 60 * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentShift, sendHeartbeatWithLocation]);

  return {
    sendManualHeartbeat: sendHeartbeatWithLocation,
  };
}
